import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@mui/material'
import { toast } from 'react-toastify'
import { BACKEND_URI } from '../config'



function Login({ onLogin, phonenumber, setPhoneNumber }) {
  const navigate = useNavigate()
  const [username, setUsername] = useState('')
  const [firstname, setFirstName] = useState('')
  const [lastname, setLastName] = useState('')
  const [password, setPassword] = useState('')
  const [inviteCode, setInviteCode] = useState('')
  const [email, setEmail] = useState('')
  const [passwordStrength, setPasswordStrength] = useState('')
  const [isSignup, setIsSignup] = useState(false) // Toggle between login and signup
  const [loginError, setloginError] = useState('')
  const [forgotUsername, setForgotUsername] = useState(false)
  const [forgotPassword, setForgotPassword] = useState(false)
  const [resendDisabled, setResendDisabled] = useState(false)
  const [resendTimer, setResendTimer] = useState(45) // Initial countdown

  useEffect(() => {
    let interval
    if (resendDisabled) {
      interval = setInterval(() => {
        setResendTimer(prev => {
          if (prev <= 1) {
            clearInterval(interval)
            setResendDisabled(false) // Enable button when timer ends
            return 45 // Reset timer for next use
          }
          return prev - 1
        })
      }, 1000)
    }
    return () => clearInterval(interval) // Cleanup on unmount
  }, [resendDisabled]) // ✅ Only runs when resendDisabled changes

  const handlePasswordChange = e => {
    const pwd = e.target.value
    setPassword(pwd)

    // Validate password strength
    if (pwd.length < 6) {
      setPasswordStrength('Weak')
    } else if (/[A-Z]/.test(pwd) && /[0-9]/.test(pwd)) {
      setPasswordStrength('Strong')
    } else {
      setPasswordStrength('Medium')
    }
  }

  const isValidEmail = email => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const handleForgotUsername = async e => {
    try {
      const response = await fetch(
        `${BACKEND_URI}/api/send-username-email`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email: email }),
        }
      )

      const data = await response.json()

      // Handle response
      if (!response.ok) {
        throw new Error(data.error || 'An error occurred')
      }

      toast.success('Email sent with username')

      setForgotUsername(false)
      setloginError('Your username has been sent to your email.')
      setEmail('')
    } catch (err) {
      setloginError(err.message || 'Invalid username, password, or invite code')
    }
  }

  const handleForgotPassword = async e => {
    try {
      const response = await fetch(`${BACKEND_URI}/api/password-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email }),
      })

      const data = await response.json()

      // Handle response
      if (!response.ok) {
        throw new Error(data.error || 'An error occurred')
      }

      toast.success('Email sent with password reset link')

      setForgotPassword(false)
      setloginError('A password reset link has been sent to your email.')
      setEmail('')
    } catch (err) {
      setloginError(err.message || 'Invalid username, password, or invite code')
    }
  }

  const handleSubmit = async e => {
    e.preventDefault()

    // Validate fields
    if (
      !username ||
      !password ||
      (isSignup && !inviteCode) ||
      (isSignup && !email) ||
      (isSignup && !firstname) ||
      (isSignup && !lastname) ||
      (isSignup && !phonenumber)
    ) {
      setloginError('Please fill in all fields.')
      return
    }

    if (isSignup && !isValidEmail(email)) {
      setloginError('Invalid Email Address')
      return
    }

    // Determine the endpoint
    const endpoint = isSignup ? '/api/signup' : '/api/login'
    const payload = isSignup
      ? {
          username,
          password,
          inviteCode,
          email,
          firstname,
          lastname,
          phonenumber,
        }
      : { username, password }

    try {
      const response = await fetch(`${BACKEND_URI}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })

      const data = await response.json()

      // Handle response
      if (!response.ok) {
        throw new Error(data.error || 'An error occurred')
      }

      if (isSignup) {
        setIsSignup(false) // Switch to login mode
        navigate('/sms-verify', {})
      } else {
        setloginError('Login Successful!')
        onLogin(data.token) // Notify parent component of login
      }
    } catch (err) {
      setloginError(err.message || 'Invalid username, password, or invite code')
    }
  }

  return (
    <div style={styles.container}>
      <div style={styles.formContainer}>
        {!forgotUsername && !forgotPassword && (
          <>
            <h2>{isSignup ? 'Sign Up' : 'Login'}</h2>

            <form onSubmit={handleSubmit}>
              {isSignup && (
                <div style={styles.inputGroup}>
                  <label>Invite Code</label>
                  <input
                    type='text'
                    placeholder='Enter invite code'
                    value={inviteCode}
                    onChange={e => setInviteCode(e.target.value)}
                    style={styles.input}
                  />
                </div>
              )}

              {/* Username Field */}
              <div style={{ textAlign: 'left' }}>
                <label>Username</label>
                <input
                  type='text'
                  placeholder='Enter your username'
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  style={styles.input}
                />
              </div>
              <div style={{ marginBottom: '1rem', textAlign: 'right' }}>
                {!isSignup && (
                  <button
                    type='button'
                    onClick={e => {
                      setForgotUsername(true)
                      setloginError('')
                    }}
                    style={styles.forgotLink}
                  >
                    Forgot Username?
                  </button>
                )}
              </div>

              <div style={{ textAlign: 'left' }}>
                <label>Password</label>
                <input
                  type='password'
                  placeholder='Enter your password'
                  value={password}
                  onChange={handlePasswordChange}
                  style={styles.input}
                />
              </div>
              <div style={{ marginBottom: '1rem', textAlign: 'right' }}>
                {!isSignup && (
                  <button
                    type='button'
                    onClick={e => {
                      setForgotPassword(true)
                      setloginError('')
                    }}
                    style={styles.forgotLink}
                  >
                    Forgot Password?
                  </button>
                )}
              </div>

              <div style={styles.inputGroup}>
                {isSignup && password && (
                  <small
                    style={{ color: passwordStrengthColor(passwordStrength) }}
                  >
                    {passwordStrength && `Strength: ${passwordStrength}`}
                  </small>
                )}
                {isSignup && (
                  <div style={styles.nameContainer}>
                    <div style={styles.nameInputGroup}>
                      <label>First Name</label>
                      <input
                        type='text'
                        placeholder='Enter your first name'
                        value={firstname}
                        onChange={e => setFirstName(e.target.value)}
                        style={styles.input}
                      />
                    </div>
                    <div style={styles.nameInputGroup}>
                      <label>Last Name</label>
                      <input
                        type='text'
                        placeholder='Enter your last name'
                        value={lastname}
                        onChange={e => setLastName(e.target.value)}
                        style={styles.input}
                      />
                    </div>
                  </div>
                )}
                {isSignup && (
                  <div style={styles.inputGroup}>
                    <label>Email</label>
                    <input
                      type='text'
                      placeholder='Enter email'
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      style={styles.input}
                    />
                  </div>
                )}
                {isSignup && (
                  <div style={styles.inputGroup}>
                    <label>Phone Number</label>
                    <input
                      type='text'
                      placeholder='1234567890'
                      value={phonenumber}
                      onChange={e => {
                        const value = e.target.value
                        if (/^\d*$/.test(value) && value.length <= 10) {
                          setPhoneNumber(value)
                        }
                      }}
                      style={styles.input}
                    />
                  </div>
                )}
              </div>
              <button type='submit' style={styles.button}>
                {isSignup ? 'Sign Up' : 'Login'}
              </button>
            </form>

            <p style={styles.toggleText}>
              {isSignup
                ? 'Already have an account? '
                : "Don't have an account? "}
              <span
                style={styles.toggleLink}
                onClick={() => {
                  setloginError('')
                  setIsSignup(!isSignup) // Toggle between login and signup
                  setEmail('')
                  setUsername('') // Clear the username field
                  setPassword('') // Clear the password field
                  setInviteCode('') // Clear the invite code field
                }}
              >
                {isSignup ? 'Log in' : 'Sign up'}
              </span>
            </p>
          </>
        )}

        {(forgotPassword || forgotUsername) && (
          <>
            <div style={styles.inputGroup}>
              <label>Email</label>
              <input
                type='text'
                placeholder='Enter your email.'
                value={email}
                onChange={e => setEmail(e.target.value)}
                style={styles.input}
              />
            </div>
            <Button
              onClick={e => {
                forgotPassword ? handleForgotPassword() : handleForgotUsername()
              }}
              variant='contained'
              color='primary'
              style={{ marginRight: '10px' }}
              disabled={resendDisabled}
            >
              {resendDisabled ? `Resend in ${resendTimer}s` : 'Send Email'}
            </Button>

            <Button
              onClick={e => {
                forgotPassword
                  ? setForgotPassword(false)
                  : setForgotUsername(false)
                setloginError('')
              }}
              variant='contained'
              color='error'
              style={styles.changePasswordButton}
            >
              Cancel
            </Button>
          </>
        )}
        <div />
        <small style={{ color: loginlogColor(loginError) }}>{loginError}</small>
      </div>
    </div>
  )
}

// Helper function for password strength colors
const passwordStrengthColor = strength => {
  if (strength === 'Weak') return 'red'
  if (strength === 'Medium') return 'orange'
  if (strength === 'Strong') return 'green'
  return 'black'
}

const loginlogColor = message => {
  if (message === 'Login Successful!') return 'green'
  return 'red'
}

const styles = {
  forgotLink: {
    background: 'none',
    border: 'none',
    color: '#007BFF',
    cursor: 'pointer',
    fontSize: '14px',
    marginTop: '5px',
    // padding: "5px",
    // display: "block",  // Forces the button to take full width
    textAlign: 'right', // Aligns text inside button to the right
    // width: "100%", // Ensures alignment works properly
  },
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    backgroundColor: '#f5f5f5',
  },
  formContainer: {
    backgroundColor: '#fff',
    padding: '2rem',
    borderRadius: '8px',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
    width: '300px',
    textAlign: 'center',
  },
  nameContainer: {
    display: 'flex', // Enable flexbox
    justifyContent: 'space-between', // Add space between the inputs
    gap: '1rem', // Space between First Name and Last Name inputs
    marginBottom: '1rem', // Add space below the row
    marginTop: '1rem',
  },
  nameInputGroup: {
    flex: 1, // Make each input take equal space
    textAlign: 'left',
  },
  inputGroup: {
    marginBottom: '1rem',
    textAlign: 'left',
  },
  input: {
    width: '100%',
    padding: '0.5rem',
    borderRadius: '4px',
    border: '1px solid #ccc',
    marginTop: '0.5rem',
    boxSizing: 'border-box',
  },
  button: {
    width: '100%',
    padding: '0.75rem',
    borderRadius: '4px',
    border: 'none',
    backgroundColor: '#007BFF',
    color: '#fff',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  toggleText: {
    marginTop: '1rem',
    fontSize: '0.9rem',
  },
  toggleLink: {
    color: '#007BFF',
    cursor: 'pointer',
    textDecoration: 'underline',
  },
}

export default Login
